
import React, { useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { ArrowRight, Building, HandHeart, Users, Quote } from 'lucide-react';
import { Link } from 'react-router-dom';
import PageHeader from '@/components/PageHeader';

const About = () => {
  // Scroll to top when page loads
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return <div className="flex flex-col min-h-screen">
    <main className="flex-grow">
      {/* Hero section with black background and wave */}
      <PageHeader icon={<Building size={16} />} badgeText="The Lovabuild Story" title={<><span className="text-gold">Turning Simplicity into Digital Luxury</span></>} subtitle="At Lovabuild, we're rewriting the rules of web development. No more overpriced agencies, 
                  no more complexity—just beautifully crafted apps, delivered with AI precision, at an unbelievable price." />

      {/* Split content section - Replacing the original hero */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-6">
          <div className="flex flex-col md:flex-row items-center gap-12">
            {/* Left side - Text content (simplified since we moved content to header) */}
            <div className="w-full md:w-1/2 space-y-6">
              <h2 className="text-3xl font-bold leading-tight">
                Our <span className="text-gold">Mission</span>
              </h2>

              <p className="text-lg text-luxury-600 leading-relaxed">Lovabuild was created to flip the script on outdated, draggy development cycles.
                We don't do slow. We don't do bloated.
                We help visionary founders and bold businesses go from idea to launch at record speed — with clean, high-performance MVPs and internal tools built using cutting-edge AI and elite UX.

                But we don't stop at building. We protect AI-generated code with comprehensive security audits, ensuring your "vibe-coded" MVP doesn't become a liability. And we automate WordPress workflows with intelligent plugin agents that save you hours every week.

                Forget the back-and-forth. Forget the delays.
                This is fast, focused, functional product development — designed to get you in the market, get user feedback, and grow securely.</p>

              <div className="pt-4">
                <Link to="/services" onClick={() => window.scrollTo(0, 0)}>
                  <Button size="lg" className="bg-gold hover:bg-gold/90 text-white premium-button group">
                    Let's Build Something Beautiful
                    <ArrowRight className="ml-2 h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
                  </Button>
                </Link>
              </div>
            </div>

            {/* Right side - Image */}
            <div className="w-full md:w-1/2">
              <div className="relative rounded-2xl overflow-hidden shadow-luxury">
                <img src="/lovable-uploads/c9fb3907-75be-47d7-b746-b5f84c0a3c64.png" alt="We don't do slow. We don't do bloated." className="w-full h-auto object-cover aspect-[4/3]" />
                <div className="absolute inset-0 bg-gradient-to-tr from-luxury-900/30 to-transparent"></div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Our Values section */}
      <section className="py-20 bg-luxury-50">
        <div className="container mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold mb-4">Our Core Values</h2>
            <p className="text-luxury-600 max-w-2xl mx-auto">
              The principles that guide everything we do at Lovabuild
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {/* Value 1 */}
            <div className="bg-white p-8 rounded-xl shadow-luxury flex flex-col items-center text-center">
              <div className="bg-highlight/10 p-4 rounded-full mb-6">
                <HandHeart className="h-8 w-8 text-highlight" />
              </div>
              <h3 className="text-xl font-bold mb-3">Craftsmanship</h3>
              <p className="text-luxury-600">
                We believe in creating digital experiences that are not just functional,
                but beautiful, memorable and crafted with precision.
              </p>
            </div>

            {/* Value 2 */}
            <div className="bg-white p-8 rounded-xl shadow-luxury flex flex-col items-center text-center">
              <div className="bg-highlight/10 p-4 rounded-full mb-6">
                <Users className="h-8 w-8 text-highlight" />
              </div>
              <h3 className="text-xl font-bold mb-3">Accessibility</h3>
              <p className="text-luxury-600">
                Luxury web development shouldn't be exclusive. We're committed to making
                high-end solutions accessible to businesses of all sizes.
              </p>
            </div>

            {/* Value 3 */}
            <div className="bg-white p-8 rounded-xl shadow-luxury flex flex-col items-center text-center">
              <div className="bg-highlight/10 p-4 rounded-full mb-6">
                <ArrowRight className="h-8 w-8 text-highlight" />
              </div>
              <h3 className="text-xl font-bold mb-3">Forward Thinking</h3>
              <p className="text-luxury-600">
                We embrace AI and emerging technologies to deliver better results, faster,
                without sacrificing quality or attention to detail.
              </p>
            </div>

            {/* Value 4 - NEW */}
            <div className="bg-white p-8 rounded-xl shadow-luxury flex flex-col items-center text-center border-2 border-blue-500/20">
              <div className="bg-blue-500/10 p-4 rounded-full mb-6">
                <Building className="h-8 w-8 text-blue-500" />
              </div>
              <h3 className="text-xl font-bold mb-3">Security-First</h3>
              <p className="text-luxury-600">
                We don't just build fast—we build secure. Every line of AI-generated code is vetted for vulnerabilities.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Our Approach section - Fixed text colors for better visibility */}
      <section className="py-20 bg-black">
        <div className="container mx-auto px-6">
          <div className="flex flex-col md:flex-row items-center gap-12">
            {/* Left side - Image */}
            <div className="w-full md:w-1/2">
              <div className="relative rounded-2xl overflow-hidden shadow-luxury">
                <img src="https://images.unsplash.com/photo-1488590528505-98d2b5aba04b" alt="Technology workspace" className="w-full h-auto object-cover aspect-[4/3]" />
                <div className="absolute inset-0 bg-gradient-to-tr from-luxury-900/30 to-transparent"></div>
              </div>
            </div>

            {/* Right side - Text content with improved visibility */}
            <div className="w-full md:w-1/2 space-y-6">
              <h2 className="text-3xl md:text-4xl font-bold leading-tight text-white">
                Our <span className="text-gold">Approach</span>
              </h2>

              <p className="text-lg text-white/90 leading-relaxed">
                We combine human creativity with AI efficiency to deliver exceptional web applications
                that would traditionally cost 5-10x more. Our streamlined process maintains the highest
                standards while eliminating the bloat and overhead of traditional agencies.
              </p>

              <ul className="space-y-4">
                <li className="flex items-start">
                  <span className="bg-gold/20 p-1 rounded-full text-gold mr-3 mt-1">
                    <ArrowRight className="h-4 w-4" />
                  </span>
                  <span className="text-white/90">AI-powered development that accelerates production without sacrificing quality</span>
                </li>
                <li className="flex items-start">
                  <span className="bg-gold/20 p-1 rounded-full text-gold mr-3 mt-1">
                    <ArrowRight className="h-4 w-4" />
                  </span>
                  <span className="text-white/90">Expert human oversight ensuring every pixel is perfect</span>
                </li>
                <li className="flex items-start">
                  <span className="bg-gold/20 p-1 rounded-full text-gold mr-3 mt-1">
                    <ArrowRight className="h-4 w-4" />
                  </span>
                  <span className="text-white/90">Transparent pricing with no hidden fees or surprises</span>
                </li>
              </ul>

              <div className="pt-4">
                <Link to="/services" onClick={() => window.scrollTo(0, 0)}>
                  <Button size="lg" className="bg-gold hover:bg-gold/90 text-white premium-button group">
                    Explore Our Services
                    <ArrowRight className="ml-2 h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
                  </Button>
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Wole Akinloye - One Man Team Section */}
      <section className="py-20 bg-gradient-to-br from-gray-50 to-white">
        <div className="container mx-auto px-6">
          <div className="max-w-5xl mx-auto">
            {/* Section Header */}
            <div className="text-center mb-16">
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-gold/10 text-gold text-sm font-medium mb-6">
                <Users size={16} />
                <span>Meet The Creator</span>
              </div>
              <h2 className="text-4xl md:text-5xl font-bold mb-4 text-black">
                The <span className="text-gold-dark">AI With The Touch of Human</span> Powerhouse
              </h2>
              <p className="text-xl text-gray-600 max-w-3xl mx-auto">For startups and growing businesses that need smart, scalable apps and internal tools—without the bloated agency fees or endless delays</p>
            </div>

            {/* Main Content */}
            <div className="flex flex-col lg:flex-row items-center gap-12 lg:gap-20">
              {/* Image Side */}
              <div className="w-full lg:w-1/3">
                <div className="relative">
                  {/* Decorative background */}
                  <div className="absolute -inset-4 bg-gradient-to-r from-gold/20 to-transparent rounded-2xl blur-xl"></div>
                  <div className="relative bg-white p-2 rounded-2xl shadow-2xl">
                    <img src="/lovable-uploads/165bbe9e-318f-45ad-8db6-63d8975589f9.png" alt="Wole Akinloye - Creator of Lovabuild" className="w-full h-auto rounded-xl object-cover aspect-[3/4]" />
                  </div>
                </div>
              </div>

              {/* Content Side */}
              <div className="w-full lg:w-2/3 space-y-8">
                <div>
                  <h3 className="text-3xl md:text-4xl font-bold text-black mb-2">
                    Wole Akinloye
                  </h3>
                  <p className="text-gold text-lg font-medium mb-6">AI Freak</p>
                </div>

                {/* Quote */}
                <div className="relative">
                  <Quote className="absolute -top-2 -left-2 h-8 w-8 text-gold/30" />
                  <blockquote className="text-xl md:text-2xl text-gray-700 italic leading-relaxed pl-8">
                    "I built Lovabuild because I was tired of watching brilliant ideas die in development hell.
                    Every founder deserves to see their vision come to life—fast, beautiful, and affordable."
                  </blockquote>
                </div>

                {/* Key Points */}
                <div className="grid md:grid-cols-2 gap-6 pt-4">
                  <div className="flex items-start space-x-3">
                    <div className="bg-gold/20 p-2 rounded-lg">
                      <ArrowRight className="h-5 w-5 text-gold" />
                    </div>
                    <div>
                      <h4 className="font-semibold text-black">AI-First Visionary</h4>
                      <p className="text-gray-600 text-sm">Pioneering the future of web development</p>
                    </div>
                  </div>
                  <div className="flex items-start space-x-3">
                    <div className="bg-gold/20 p-2 rounded-lg">
                      <HandHeart className="h-5 w-5 text-gold" />
                    </div>
                    <div>
                      <h4 className="font-semibold text-black">Craft Obsessed</h4>
                      <p className="text-gray-600 text-sm">Every pixel, every interaction matters</p>
                    </div>
                  </div>
                  <div className="flex items-start space-x-3">
                    <div className="bg-blue-500/20 p-2 rounded-lg">
                      <Building className="h-5 w-5 text-blue-500" />
                    </div>
                    <div>
                      <h4 className="font-semibold text-black">Security Expert</h4>
                      <p className="text-gray-600 text-sm">Protecting AI-generated code from vulnerabilities</p>
                    </div>
                  </div>
                  <div className="flex items-start space-x-3">
                    <div className="bg-purple-500/20 p-2 rounded-lg">
                      <Building className="h-5 w-5 text-purple-500" />
                    </div>
                    <div>
                      <h4 className="font-semibold text-black">WordPress AI Specialist</h4>
                      <p className="text-gray-600 text-sm">Automating WordPress with intelligent plugins</p>
                    </div>
                  </div>
                </div>

                {/* Call to Action */}
                <div className="pt-6">
                  <Link to="/services" onClick={() => window.scrollTo(0, 0)}>
                    <Button size="lg" className="bg-black hover:bg-gray-800 text-white premium-button group">
                      Start Your Project Today
                      <ArrowRight className="ml-2 h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
                    </Button>
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </main>
  </div>;
};

export default About;
